var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { poisListToGeoJson } from '../../utils/poi';
import './popup/PoiPopup';
const PIN_DEFAULT_COLOR = '#08c1ff';
const PIN_DEFAULT_IMAGE_ID = 'poi-pin';
const PIN_STATUS_COLORS = {
    in_progress: '#FFC85B',
    validated: PIN_DEFAULT_COLOR,
};
const PIN_STATUS_IMAGE_IDS = {
    in_progress: 'poi-pin-in_progress',
};
let PoiMap = class PoiMap extends LitElement {
    constructor() {
        super(...arguments);
        this.pois = [];
        this.selectedPoi = null;
        this.mapOptions = {};
        this.logoUrl = null;
        this.popupTagName = 'poi-popup';
        this.mapInstance = null;
        this.mapLib = null;
    }
    updated(_changedProperties) {
        if (_changedProperties.has('pois')) {
            this.drawPois();
            this.flyToPois();
        }
        if (_changedProperties.has('selectedPoi')) {
            this.selectPoi();
        }
        if (_changedProperties.has('logoUrl')) {
            this.loadPinImages();
        }
    }
    getStyleFontStack(weight = 'regular') {
        const style = this.mapInstance.getStyle();
        const targetWeight = weight === 'bold' ? /bold/i : /regular|normal/i;
        if (style?.layers) {
            for (const layer of style.layers) {
                const font = layer.layout?.['text-font'];
                if (font && Array.isArray(font)) {
                    const match = font.find((f) => targetWeight.test(f));
                    if (match)
                        return [match];
                }
            }
            // Fallback: retourne la première font trouvée
            for (const layer of style.layers) {
                const font = layer.layout?.['text-font'];
                if (font && Array.isArray(font))
                    return font;
            }
        }
        return ['Arial Unicode MS Regular'];
    }
    buildPinSvg(color) {
        if (this.logoUrl) {
            return `
    <svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 22.5 30">
      <path style="fill:${color};" d="M11.25,0C5.04,0,0,5.04,0,11.25c0,2.68.91,5.28,2.59,7.37l8.66,11.38,8.66-11.38c1.67-2.09,2.59-4.69,2.59-7.37C22.5,5.04,17.46,0,11.25,0Z"/>
      <circle style="fill:#fff;" cx="11.25" cy="11.25" r="8"/>
      <clipPath id="logoClip"><circle cx="11.25" cy="11.25" r="7"/></clipPath>
      <image href="${this.logoUrl}" x="4.25" y="4.25" width="16" height="16" clip-path="url(#logoClip)" preserveAspectRatio="xMidYMid slice"/>
    </svg>
    `;
        }
        return `
    <svg viewBox="0 0 16 16" width="100" height="100" xmlns="http://www.w3.org/2000/svg">
      <path d="M8 0C4.686 0 2 2.686 2 6c0 1.43.486 2.816 1.379 3.932L8 16l4.621-6.068C13.514 8.816 14 7.43 14 6c0-3.314-2.686-6-6-6z" fill="${color}"/>
      <circle cx="8" cy="6" r="2" fill="#ffffff"/>
    </svg>
    `;
    }
    loadPinImage(imageId, color) {
        return new Promise((resolve) => {
            const svg = this.buildPinSvg(color);
            const pinImage = new Image();
            pinImage.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
            pinImage.onload = () => {
                if (this.mapInstance.hasImage(imageId)) {
                    this.mapInstance.removeImage(imageId);
                }
                this.mapInstance.addImage(imageId, pinImage, { sdf: false });
                resolve();
            };
        });
    }
    async loadPinImages() {
        if (!this.mapInstance)
            return;
        const imagesToLoad = [
            [PIN_DEFAULT_IMAGE_ID, PIN_DEFAULT_COLOR],
            ...Object.entries(PIN_STATUS_IMAGE_IDS).map(([status, imageId]) => [imageId, PIN_STATUS_COLORS[status]]),
        ];
        await Promise.all(imagesToLoad.map(([id, color]) => this.loadPinImage(id, color)));
        this.drawPois();
    }
    drawPois() {
        if (!this.mapInstance)
            return;
        // Filtrer les points sans coordonnées
        const validPois = this.pois.filter(poi => (poi.longitude != null && poi.latitude != null));
        const geojson = poisListToGeoJson(validPois);
        // Ajouter "isSelected" et "status" (extrait de poi.properties.status)
        geojson.features = geojson.features.map((f) => {
            const poi = validPois.find((p) => p.id === f.properties.id);
            return {
                ...f,
                properties: {
                    ...f.properties,
                    isSelected: this.selectedPoi?.id === f.properties.id,
                    status: poi?.properties?.status ?? null,
                },
            };
        });
        // Supprimer les anciennes couches/sources
        ['clusters', 'cluster-count', 'unclustered-point'].forEach((id) => {
            if (this.mapInstance.getLayer(id))
                this.mapInstance.removeLayer(id);
        });
        if (this.mapInstance.getSource('pois-source'))
            this.mapInstance.removeSource('pois-source');
        // Ajouter la source
        this.mapInstance.addSource('pois-source', {
            type: 'geojson',
            data: geojson,
            cluster: true,
            clusterMaxZoom: 14,
            clusterRadius: 50,
        });
        // Clusters
        this.mapInstance.addLayer({
            id: 'clusters',
            type: 'circle',
            source: 'pois-source',
            filter: ['has', 'point_count'],
            paint: {
                'circle-color': ['step', ['get', 'point_count'], '#FFB300', 10, '#FB8C00', 50, '#E65100'],
                'circle-radius': ['step', ['get', 'point_count'], 15, 10, 20, 50, 30],
            },
        });
        // Comptes des clusters — utilise les fonts du style chargé pour compatibilité
        const fontStack = this.getStyleFontStack('bold');
        this.mapInstance.addLayer({
            id: 'cluster-count',
            type: 'symbol',
            source: 'pois-source',
            filter: ['has', 'point_count'],
            layout: {
                'text-field': '{point_count_abbreviated}',
                'text-font': fontStack,
                'text-size': 13,
            },
            paint: {
                'text-color': '#ffffff',
            },
        });
        // Points non clusterisés — icône choisie en fonction du status (fallback: pin par défaut)
        const iconImageExpression = ['match', ['get', 'status']];
        Object.entries(PIN_STATUS_IMAGE_IDS).forEach(([status, imageId]) => {
            iconImageExpression.push(status, imageId);
        });
        iconImageExpression.push(PIN_DEFAULT_IMAGE_ID);
        this.mapInstance.addLayer({
            id: 'unclustered-point',
            type: 'symbol',
            source: 'pois-source',
            filter: ['!', ['has', 'point_count']],
            layout: {
                'icon-image': iconImageExpression,
                'icon-size': 0.3,
                'icon-anchor': 'bottom',
                'icon-allow-overlap': true,
            },
        });
        // Gestion des clics pour la sélection et les popups
        this.mapInstance.on('click', 'unclustered-point', (e) => {
            const feature = e.features[0];
            const clickedPoi = this.pois.find(p => p.id === feature.properties.id);
            if (!clickedPoi)
                return;
            this.selectedPoi = clickedPoi;
            this.dispatchEvent(new CustomEvent('poi-selected', { detail: { poi: clickedPoi } }));
            if (!this.popupTagName)
                return;
            const popupComponent = document.createElement(this.popupTagName);
            popupComponent.poi = clickedPoi;
            popupComponent.logoUrl = this.logoUrl;
            ['edit-poi', 'delete-poi', 'view-poi'].forEach((evtName) => {
                popupComponent.addEventListener(evtName, (event) => {
                    this.dispatchEvent(new CustomEvent(evtName, { detail: event.detail }));
                });
            });
            const tempDiv = document.createElement('div');
            tempDiv.appendChild(popupComponent);
            new this.mapLib.Popup({ offset: 25, maxWidth: "500px" })
                .setLngLat([clickedPoi.longitude, clickedPoi.latitude])
                .setDOMContent(tempDiv)
                .addTo(this.mapInstance);
        });
        // Curseur au survol
        this.mapInstance.on('mouseenter', 'unclustered-point', () => {
            this.mapInstance.getCanvas().style.cursor = 'pointer';
        });
        this.mapInstance.on('mouseleave', 'unclustered-point', () => {
            this.mapInstance.getCanvas().style.cursor = '';
        });
    }
    selectPoi() {
        if (!this.mapInstance || !this.selectedPoi)
            return;
        const coordinates = [this.selectedPoi.longitude, this.selectedPoi.latitude];
        if (coordinates[0] == null || coordinates[1] == null)
            return;
        this.mapInstance.flyTo({ center: coordinates, zoom: 14 });
    }
    async handleMapReady(event) {
        this.mapInstance = event.detail.map;
        this.mapLib = event.detail.mapLib;
        await this.loadPinImages();
        this.selectPoi();
        this.dispatchEvent(new CustomEvent('map-loaded', {
            detail: {
                map: this.mapInstance,
                mapLib: this.mapLib,
            }
        }));
    }
    flyToPois() {
        if (!this.mapInstance || this.pois.length === 0)
            return;
        const bounds = new this.mapLib.LngLatBounds();
        this.pois.forEach((poi) => {
            if (poi.longitude != null && poi.latitude != null) {
                bounds.extend([poi.longitude, poi.latitude]);
            }
        });
        this.mapInstance.fitBounds(bounds, { padding: 50 });
    }
    getMapInstance() { return this.mapInstance; }
    getMapLib() { return this.mapLib; }
    render() {
        return html `
      <basic-map
        .mapOptions=${this.mapOptions}
        @map-loaded=${this.handleMapReady}
      >
        <slot></slot>
      </basic-map>
    `;
    }
};
PoiMap.styles = css `
    :host {
      display: block;
      width: 100%;
      height: 100%;
    }
    basic-map {
      width: 100%;
      height: 100%;
    }
  `;
__decorate([
    property({ type: Array })
], PoiMap.prototype, "pois", void 0);
__decorate([
    property({ type: Object })
], PoiMap.prototype, "selectedPoi", void 0);
__decorate([
    property({ type: Object })
], PoiMap.prototype, "mapOptions", void 0);
__decorate([
    property({ type: String })
], PoiMap.prototype, "logoUrl", void 0);
__decorate([
    property({ type: String, attribute: 'popup-tag-name' })
], PoiMap.prototype, "popupTagName", void 0);
__decorate([
    state()
], PoiMap.prototype, "mapInstance", void 0);
PoiMap = __decorate([
    customElement('poi-map')
], PoiMap);
export { PoiMap };
//# sourceMappingURL=PoiMap.js.map